import { useCallback, useMemo, useRef, useState, type ReactNode } from "react";
import { clamp } from "../../lib/utils";

import { Box } from "../../components/ui/box";
import type { SurfaceToken } from "../../components/ui/box-classes";
import { useElementSize } from "../../hooks/use-element-size";
import { useIsMobile } from "../../hooks/use-media-query";
import {
  MARQUEE_SURFACE_ATTR,
  SPACING_SCALE,
  SortableGrid,
  SortableGridItem,
  resizeItem,
  type GridColumns,
  type GridFlow,
  type GridLayoutItem,
  type GridRect,
  type SortableGridDragHandleProps,
  type SpacingToken,
  type SpanConstraints,
} from "../sortable-grid";
import { WidgetInstanceProvider } from "./contexts";
import { useWidgetRegistry } from "./registry-context";
import type { WidgetSpan, WidgetConfig, WidgetInstance, WidgetMove, WidgetSurface } from "./types";
import { WidgetCard, type WidgetWidthOption } from "./widget-card";
import type { Omittable } from "../../lib/props";

/** Per-instance render args the grid hands a card slot. Structural/manage
 *  callbacks are only defined when the HOST wires the matching grid prop — an
 *  `undefined` callback hides that affordance on the card. */
export type WidgetCardSlotProps = Omittable<{
  instance: WidgetInstance;
  canManage: boolean;
  /** Layout authoring split from manage chrome (managed surfaces arrange
   *  without managing; mobile arranges nothing). */
  canArrange?: boolean;
  /** The viewer may only read the widgets (no module header controls). */
  consumeOnly?: boolean;
  dragHandleProps?: SortableGridDragHandleProps;
  isDragging?: boolean;
  isResizing?: boolean;
  liveW: number;
  liveH: number;
  onMaximize?: () => void;
  onConfigure?: () => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  /** Only defined when the host wires {@link WidgetGridProps.onReplace} — an
   *  `undefined` callback hides the card's Replace menu item. */
  onReplace?: () => void;
  /** Only defined when the host wires {@link WidgetGridProps.onConvertToCustom}. */
  onConvertToCustom?: () => void;
  onRename?: (title: string) => void;
  onConfigChange?: (patch: WidgetConfig) => void;
  /** Snap the tile back to its widget definition's default span. */
  onResetSize?: () => void;
  /** Host-owned discrete width menu (only on hosts that pass `widthOptions`). */
  widthOptions?: readonly WidgetWidthOption[];
  onWidthChange?: (width: number) => void;
  /** AutoHeight widgets only: reports the body's natural px height; the grid
   *  converts to whole rows and feeds its derived live layout. `snap` marks a
   *  load-time settle — the commit reflows without the FLIP spring. */
  onContentHeightChange?: (px: number, opts?: { snap?: boolean }) => void;
}>;

export type WidgetGridProps = Omittable<{
  instances: WidgetInstance[];
  canManage: boolean;
  /** Layout authoring (drag/resize gestures) split from `canManage` — managed
   *  surfaces hide manage chrome but keep arranging. Defaults to `canManage`.
   *  The grid itself suspends arranging while it renders the single-column
   *  stack (mobile viewport or a too-narrow board container) — a stack has no
   *  x-axis to author. */
  canArrange?: boolean;
  /** The viewer may only READ every widget on this board — forwarded to each
   *  card, which drops the module's transient header controls. */
  consumeOnly?: boolean;
  /** Which surface every card on this board paints (host-owned axis) —
   *  forwarded to the default {@link WidgetCard}; custom `renderCard` slots
   *  pass their own. */
  surface?: WidgetSurface;
  /** The surface THIS BOARD paints, which `surface="canvas"` cards fill
   *  exactly so they mask the authoring guide dots. Defaults to `background` —
   *  a board is a page. A board mounted inside a card or a dialog names that
   *  surface instead. Forwarded to the default {@link WidgetCard}. */
  canvas?: SurfaceToken;
  /** Host-owned width presets offered in every card's ellipsis menu, beside
   *  the side-edge drag every width-resizable card keeps (autoHeight cards
   *  included — only their vertical handles are gone). Presets are shortcuts,
   *  not a constraint: the drag commits any span-legal width. Commits go
   *  through the normal resize/reflow path. */
  widthOptions?: readonly WidgetWidthOption[];
  columns?: number;
  rowHeight?: number;
  /** Inter-tile gap forwarded to the grid (defaults to `md`). */
  gap?: SpacingToken;
  /** The board's ONE default height for CARDS, in rows — what a reset-size
   *  returns a card to (clamped to its span). A host derives it once from a
   *  target pixel height with {@link rowsNearestHeight} and uses the same
   *  number when it adds a widget, so every card opens at one rung. A span
   *  that names its own `defaultH` (a tile) keeps that instead. */
  defaultRows?: number;
  /** Reflow discipline forwarded to the grid: `'gravity'` (default) compacts
   *  vertically; `'dense'` keeps the layout packed tight at all times. */
  flow?: GridFlow;
  // Structural/action handlers are ALL optional — a static, read-only render
  // (read-only hosts, settings previews) simply omits them.
  /** The rects a commit changed, diffed against the stored layout. Fires only
   *  when that diff is non-empty: a drop on the tile's own cell or a width
   *  pick of the current width changes nothing and is not a move — a host
   *  that stages edits would otherwise mark its board dirty with nothing to
   *  save. */
  onMove?: (moves: WidgetMove[]) => void;
  /** Full next-layout on EVERY commit, no-op gestures included, alongside the
   *  diffed `onMove`. History consumers need the whole layout (and skip
   *  no-ops themselves); persistence keeps using moves. */
  onLayoutChange?: (next: GridLayoutItem[]) => void;
  onDelete?: (id: string) => void;
  onDuplicate?: (instance: WidgetInstance) => void;
  /** Swap a placed widget for a different module in place (the host owns the
   *  picker). Omitting it hides the card menu's Replace item. */
  onReplace?: (instance: WidgetInstance) => void;
  /** Drop a curated instance's preset lock (see `WidgetCatalogEntry.locks`).
   *  Omitting it hides the card menu's Convert-to-custom item. */
  onConvertToCustom?: (instance: WidgetInstance) => void;
  onMaximize?: (instance: WidgetInstance) => void;
  onConfigure?: (instance: WidgetInstance) => void;
  onRename?: (id: string, title: string) => void;
  onConfigChange?: (id: string, patch: WidgetConfig) => void;
  /** Override the card chrome (defaults to the kit's {@link WidgetCard}). */
  renderCard?: (props: WidgetCardSlotProps) => ReactNode;
  /** Accepts palette drops from an ancestor `SortableGridDndScope`: the
   *  palette item's payload, the settled top-left cell, and the sibling
   *  instances whose placement changed to make room. */
  onExternalDrop?: (
    item: unknown,
    cell: { x: number; y: number },
    siblingMoves: WidgetMove[],
  ) => void;
  /** Cell span an external palette item will occupy (defaults to 6×4). */
  externalSpan?: (item: unknown) => { w: number; h: number };
  /** A layer painted inside the grid's coordinate space (scrolls with the
   *  tiles). Align it to real cells via `useSortableGridTiles().measureTile`. */
  annotations?: ReactNode;
  /** Domain-free insertion seam: for every clean horizontal break between
   *  occupied row bands (a grid row no tile spans across), renders the
   *  consumer's affordance centered in the inter-row gutter — revealed on
   *  gutter hover (e.g. a quick-add "+"). `beforeY` is the grid row inserted
   *  content should land at. Multi-column layouts only (the single-column
   *  stack has no gutters); the grid never inserts anything itself. */
  renderRowInsertion?: (insertion: WidgetGridRowInsertion) => ReactNode;
  /** Marquee-to-add: when provided, drag-selecting open cells snaps a rect
   *  and hands it to the consumer via `onDraw` — the consumer opens its own
   *  picker (typically the full widget gallery) and places the pick at the
   *  drawn cell/size (clamping/settling the insert is the consumer store's
   *  job). The consumer holds the settled rect in `pending` so it stays
   *  painted while that picker is open; clearing it un-paints. The grid
   *  itself gates the gesture on canManage/desktop. */
  areaAdd?: WidgetGridAreaAdd;
  /** Live height preview in ROWS for one instance (a container growing while
   *  an eligible drag hovers it). */
  liveSpan?: { id: string; h: number } | null;
  /** Rendered auto-centered over the grid when there are zero instances. The
   *  wrapper is pointer-transparent; the node's own interactive parts stay
   *  clickable — consumers pass just their empty-state node. */
  emptyState?: ReactNode;
  /** Per-instance drag payload: when provided, a tile's drag can ride OTHER
   *  grids' external paths (dropping page tiles into containers). */
  dragItem?: (instance: WidgetInstance) => unknown;
  /** Hold the guide dots visible at rest — build surfaces (forwarded to
   *  `SortableGrid`; see its `guides` doc). Default = gesture-only reveal. */
  guides?: boolean;
  /** Plain click on open cells (not a tile) — the builder's click-off-to-
   *  deselect seam (see `SortableGrid`'s doc). */
  onBackgroundClick?: () => void;
}>;

/** One inter-row insertion point, in reading order. */
export type WidgetGridRowInsertion = {
  /** 0-based position among the grid's row breaks, top to bottom. */
  index: number;
  /** The grid row a widget inserted here should occupy (rows at/below shift). */
  beforeY: number;
};

/** Marquee-to-add configuration — see {@link WidgetGridProps.areaAdd}. */
export type WidgetGridAreaAdd = Omittable<{
  /** A marquee settled on open cells — the consumer opens its picker for the
   *  drawn rect. */
  onDraw: (area: GridRect) => void;
  /** The drawn rect the consumer is currently acting on (holds the painted
   *  selection while its picker is open); null/omitted when idle. */
  pending?: GridRect | null;
}>;

/** Mirrors `SortableGrid`'s own `rowHeight` default. */
const DEFAULT_ROW_HEIGHT = 60;

/** Rows whose rendered height (`rows × rowHeight + (rows − 1) × gap`) lands
 *  NEAREST a target pixel height — the snap point closest to "about this
 *  tall", above or below. Never fewer than one row. */
export function rowsNearestHeight(
  px: number,
  rowHeight: number,
  gap: SpacingToken = "md",
): number {
  const pitch = rowHeight + SPACING_SCALE[gap];
  return Math.max(1, Math.round((px + SPACING_SCALE[gap]) / pitch));
}

/** The board's default rows, held inside one span's `minH`/`maxH`. */
export function defaultRowsForSpan(
  rows: number,
  span: Partial<Pick<WidgetSpan, "minH" | "maxH">>,
): number {
  const max = span.maxH ?? Number.POSITIVE_INFINITY;
  return Math.min(Math.max(rows, span.minH ?? 1), Math.max(1, max));
}
const DEFAULT_COLUMN_COUNT = 12;

/** Below this measured BOARD width the multi-column cell math stops meaning
 *  anything — 12 columns land under ~40px each and cards truncate their own
 *  titles — so the grid stacks single-column. The signal is the grid's own
 *  width, never the viewport: a side panel, a split view, or a board mounted
 *  in a dialog narrows the board without the window changing. */
export const SINGLE_COLUMN_MIN_WIDTH = 640;

/** The clean horizontal break lines strictly inside the layout: grid rows no
 *  tile spans across, between the first and last occupied row. */
function rowBreaks(layout: GridLayoutItem[]): number[] {
  if (layout.length === 0) return [];
  const maxBottom = Math.max(...layout.map((i) => i.y + i.h));
  const candidates = new Set<number>();
  for (const item of layout) candidates.add(item.y + item.h);
  return [...candidates]
    .filter(
      (line) =>
        line > 0 &&
        line < maxBottom &&
        layout.every((item) => item.y + item.h <= line || item.y >= line),
    )
    .sort((a, b) => a - b);
}

/** The column counts `SortableGrid` supports, indexable so a runtime number maps
 *  to the literal-union type without an unsound cast. */
const GRID_COLUMN_COUNTS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] as const;

/** Clamp/round an arbitrary `columns` value (often server-sourced) to a column
 *  count `SortableGrid` actually supports. */
function toGridColumns(value: number): GridColumns {
  return GRID_COLUMN_COUNTS[clamp(Math.round(value), 1, GRID_COLUMN_COUNTS.length) - 1] ?? 12;
}

/** The per-instance placement changes between two layouts — the single diff
 *  shared by the kit's commit path and consumer tidy/undo/redo commits. */
export function diffMoves(prev: GridLayoutItem[], next: GridLayoutItem[]): WidgetMove[] {
  const byId = new Map(prev.map((i) => [i.id, i]));
  return next
    .filter((n) => {
      const p = byId.get(n.id);
      return p != null && (p.x !== n.x || p.y !== n.y || p.w !== n.w || p.h !== n.h);
    })
    .map((n) => ({ id: n.id, x: n.x, y: n.y, w: n.w, h: n.h }));
}

/**
 * Bridges widget instances ↔ the `SortableGrid`'s x/y/w/h layout.
 * Drag/resize commit a full next-layout; we diff it against the prior placement
 * and emit per-instance structural moves (move = x/y, resize = w/h).
 *
 * Kit invariant: instance ids are unique (optimistic inserts use
 * `temp-`-prefixed uuids, which cannot collide with server ids) — tiles key by
 * id alone so reorders never remount them (dnd registrations and FLIP
 * continuity depend on stable mounts).
 */
export function WidgetGrid({
  instances,
  canManage,
  canArrange,
  consumeOnly,
  surface,
  canvas,
  widthOptions,
  columns = DEFAULT_COLUMN_COUNT,
  rowHeight = DEFAULT_ROW_HEIGHT,
  gap = "md",
  defaultRows,
  flow,
  onMove,
  onLayoutChange,
  onDelete,
  onDuplicate,
  onReplace,
  onConvertToCustom,
  onMaximize,
  onConfigure,
  onRename,
  onConfigChange,
  renderCard,
  onExternalDrop,
  externalSpan,
  annotations,
  renderRowInsertion,
  areaAdd,
  liveSpan,
  emptyState,
  dragItem,
  guides,
  onBackgroundClick,
}: WidgetGridProps) {
  const isMobile = useIsMobile();
  // The stack decision reads the grid's OWN width, not just the viewport: a
  // wide desktop window with a side panel open leaves the board a sliver, and
  // viewport math would keep laying 12 columns into it. Width 0 means "not
  // measured yet" (the useElementSize contract) — render the desktop layout,
  // matching the SSR default, and the first observer tick (a layout effect,
  // before paint) corrects a narrow mount without a flash.
  const { ref: boardRef, size: boardSize } = useElementSize<HTMLDivElement>();
  const singleColumn =
    isMobile || (boardSize.width > 0 && boardSize.width < SINGLE_COLUMN_MIN_WIDTH);
  const registry = useWidgetRegistry();
  const gridColumns = toGridColumns(columns);
  // A single-column stack has no x-axis to author: arranging (drag/resize,
  // the grab cursor, the width menu) suspends while stacked and returns when
  // the board widens.
  const arrange = (canArrange ?? canManage) && !singleColumn;

  // Marquee-to-add: the consumer holds the settled rect (its picker acts on
  // it); the grid only needs it to paint the selection and yield overlays.
  const pendingArea = areaAdd?.pending ?? null;

  // Single column: pre-sort into (y, x) reading order BEFORE mapping to grid
  // items so DOM order = visual order in the stack (screen readers and tab
  // order walk the right sequence). Keys are stable, so a viewport or
  // container-width flip reorders without remounting. The desktop grid keeps
  // the caller's order (placement is absolute there).
  const orderedInstances = useMemo(
    () =>
      singleColumn
        ? [...instances].sort((a, b) => a.y - b.y || a.x - b.x)
        : instances,
    [instances, singleColumn],
  );

  const lookups = useMemo(() => {
    const instanceById = new Map(instances.map((i) => [i.id, i]));
    const defById = new Map(instances.map((i) => [i.id, registry.get(i.type)]));
    return { instanceById, defById };
  }, [instances, registry]);

  const constraintsFor = useCallback(
    (id: string): SpanConstraints => {
      const def = lookups.defById.get(id);
      if (!def) return {};
      const { minW, minH, maxW, maxH, lockH } = def.span;
      // AutoHeight rows are derived from content: every resize handle is
      // suppressed (autoH), and single-column renders content-sized.
      return def.autoHeight
        ? { minW, minH, maxW, maxH, lockH: true, autoH: true }
        : { minW, minH, maxW, maxH, lockH };
    },
    [lookups],
  );

  // AutoHeight: measured content rows per instance — VIEW STATE, never
  // persisted. The stored layout keeps the stored `h`; the derived layout the
  // grid renders swaps in the measured rows and reflows followers through the
  // normal whole-row compaction pass.
  const [autoRows, setAutoRows] = useState<Record<string, number>>({});
  const autoIds = useMemo(() => {
    const ids = new Set<string>();
    for (const i of instances) if (registry.get(i.type)?.autoHeight) ids.add(i.id);
    return ids;
  }, [instances, registry]);

  const storedLayout = useMemo<GridLayoutItem[]>(
    () => instances.map((i) => ({ id: i.id, x: i.x, y: i.y, w: i.w, h: i.h })),
    [instances],
  );

  // Span floors: a board saved before a definition's `minH`/`minW` rose
  // still stores the old, shorter rect. The grid never paints under a floor
  // — each short span is lifted through the same reflow a resize takes, so
  // followers move down instead of overlapping. Unlike autoHeight rows the
  // lift is NOT restored on commit: the next edit diffs against the stored
  // layout and persists the floored rect, so a stale board converges on its
  // first arrange. Derived-height tiles skip this pass (their rows follow the
  // body, floored by the same clamp below).
  const flooredLayout = useMemo<GridLayoutItem[]>(() => {
    let next = storedLayout;
    for (const { id } of storedLayout) {
      if (autoIds.has(id)) continue;
      const constraints = constraintsFor(id);
      // Re-read the rect from `next`: an earlier lift may have pushed this
      // tile down, and pinning it at its stored y would drag it back up.
      const item = next.find((i) => i.id === id);
      if (!item || (item.h >= (constraints.minH ?? 1) && item.w >= (constraints.minW ?? 1))) continue;
      next = resizeItem(next, id, item.x, item.y, item.w, item.h, gridColumns, constraints, flow);
    }
    return next;
  }, [storedLayout, autoIds, gridColumns, constraintsFor, flow]);

  const layout = useMemo<GridLayoutItem[]>(() => {
    if (autoIds.size === 0) return flooredLayout;
    let next = flooredLayout;
    for (const id of autoIds) {
      const rows = autoRows[id];
      const item = next.find((i) => i.id === id);
      if (!rows || !item || item.h === rows) continue;
      next = resizeItem(next, id, item.x, item.y, item.w, rows, gridColumns, constraintsFor(id), flow);
    }
    return next;
  }, [flooredLayout, autoIds, autoRows, gridColumns, constraintsFor, flow]);

  // px → whole rows (round UP): the ONLY place the measured px exists. The
  // rendered layout stays purely cell-based.
  const rowPx = rowHeight;
  const gapPx = SPACING_SCALE[gap];
  // Armed by a snap measurement below; the grid's next FLIP pass consumes it
  // and reflows without springing. Written inside the updater so it only arms
  // when the rows actually change (idempotent, so a replayed updater is safe);
  // a same-rows report must not leave the flag set to eat a later gesture's
  // spring.
  const snapReflowRef = useRef(false);
  const handleContentHeight = useCallback(
    (id: string, px: number, opts?: { snap?: boolean }) => {
      const rows = Math.max(1, Math.ceil((px + gapPx) / (rowPx + gapPx)));
      setAutoRows((prev) => {
        if (prev[id] === rows) return prev;
        if (opts?.snap) snapReflowRef.current = true;
        return { ...prev, [id]: rows };
      });
    },
    [rowPx, gapPx],
  );

  // The insertion gutters are pure grid math (row pitch = rowHeight + gap), so
  // no DOM measuring: each break centers its affordance in the inter-row gap.
  const insertionLayer = useMemo(() => {
    if (!renderRowInsertion || !canManage || singleColumn) return null;
    const breaks = rowBreaks(layout);
    if (breaks.length === 0) return null;
    const pitch = rowHeight + SPACING_SCALE[gap];
    const bandGapPx = SPACING_SCALE[gap];
    return breaks.map((beforeY, index) => (
      <Box pointerEvents="auto"
        key={beforeY}
        position="absolute"
        left={0}
        width="100%"
        top={beforeY * pitch - bandGapPx / 2 - 14}
        height={28}
        align="center"
        justify="center"
        // The gutter band is a pointer-events:auto strip over open cells —
        // stamping it as a marquee surface keeps drag-select armable there
        // (a <4px release stays a click, so the hover "+" is untouched).
        className="group"
        {...{ [MARQUEE_SURFACE_ATTR]: "" }}
      >
        <Box opacity={0} transition="opacity" className="group-hover:opacity-100">
          {renderRowInsertion({ index, beforeY })}
        </Box>
      </Box>
    ));
  }, [renderRowInsertion, canManage, singleColumn, layout, rowHeight, gap]);

  // Zero instances: the empty state rides the grid overlay, auto-centered.
  // The pointer-events dance lives HERE once — transparent wrappers, with the
  // node itself interactive. Hidden while a marquee rect is pending (the
  // consumer's picker is acting on it) — the panel would cover the painted
  // rect; it returns when the consumer clears the rect without a pick. Known
  // cosmetic gap: a live sweep still passes visually under the panel (the
  // sweep can only START outside it, since the panel's interactive Box eats
  // pointerdown).
  const emptyLayer =
    emptyState != null && instances.length === 0 && pendingArea == null ? (
      <Box pointerEvents="none" fill="size" flex={1}
        align="center"
        justify="center"
      >
        <Box pointerEvents="auto">{emptyState}</Box>
      </Box>
    ) : null;

  const mergedAnnotations =
    insertionLayer != null || emptyLayer != null ? (
      <>
        {annotations}
        {insertionLayer}
        {emptyLayer}
      </>
    ) : (
      annotations
    );

  // Commits write the STORED `h` back for autoHeight cards — the derived span
  // never reaches persistence or history.
  const restoreAutoHeights = useCallback(
    (next: GridLayoutItem[]): GridLayoutItem[] => {
      if (autoIds.size === 0) return next;
      return next.map((n) => {
        if (!autoIds.has(n.id)) return n;
        const stored = lookups.instanceById.get(n.id);
        return stored && stored.h !== n.h ? { ...n, h: stored.h } : n;
      });
    },
    [autoIds, lookups],
  );

  const handleLayoutChange = useCallback(
    (next: GridLayoutItem[]) => {
      const restored = restoreAutoHeights(next);
      const moves = diffMoves(storedLayout, restored);
      if (moves.length > 0) onMove?.(moves);
      onLayoutChange?.(restored);
    },
    [storedLayout, restoreAutoHeights, onMove, onLayoutChange],
  );

  const resetSize = useCallback(
    (id: string) => {
      const instance = lookups.instanceById.get(id);
      const def = lookups.defById.get(id);
      if (!instance || !def) return;
      const { defaultW, defaultH, lockH } = def.span;
      const derived = lockH || def.autoHeight;
      // A span that names its own rung (a tile) keeps it; otherwise the
      // board's one rung, clamped to the span; with neither, the rows stay.
      const restRows =
        defaultH ?? (defaultRows != null ? defaultRowsForSpan(defaultRows, def.span) : instance.h);
      const next = resizeItem(
        layout,
        id,
        instance.x,
        instance.y,
        defaultW,
        // Derived-height tiles keep their current rows — reset is width-only.
        derived ? instance.h : restRows,
        gridColumns,
        constraintsFor(id),
        flow,
      );
      handleLayoutChange(next);
    },
    [lookups, layout, gridColumns, constraintsFor, flow, handleLayoutChange, defaultRows],
  );

  // Host-owned width menu: a discrete width pick commits through the normal
  // resize/reflow path (position/height untouched; followers compact).
  const changeWidth = useCallback(
    (id: string, width: number) => {
      const item = layout.find((i) => i.id === id);
      if (!item) return;
      const next = resizeItem(
        layout,
        id,
        item.x,
        item.y,
        width,
        item.h,
        gridColumns,
        constraintsFor(id),
        flow,
      );
      handleLayoutChange(next);
    },
    [layout, gridColumns, constraintsFor, flow, handleLayoutChange],
  );

  const handleExternalDrop = useCallback(
    (item: unknown, cell: { x: number; y: number }, siblingMoves: GridLayoutItem[]) => {
      onExternalDrop?.(
        item,
        cell,
        siblingMoves.map((m) => ({ id: m.id, x: m.x, y: m.y, w: m.w, h: m.h })),
      );
    },
    [onExternalDrop],
  );

  const card =
    renderCard ?? ((props: WidgetCardSlotProps) => <WidgetCard surface={surface} canvas={canvas} {...props} />);

  return (
    <SortableGrid
      ref={boardRef}
      layout={layout}
      columns={gridColumns}
      rowHeight={rowHeight}
      gap={gap}
      singleColumn={singleColumn}
      canManage={arrange}
      flow={flow}
      constraintsFor={constraintsFor}
      onLayoutChange={handleLayoutChange}
      onExternalDrop={onExternalDrop ? handleExternalDrop : undefined}
      externalSpan={externalSpan}
      annotations={mergedAnnotations}
      guides={guides}
      onBackgroundClick={onBackgroundClick}
      onAreaSelect={areaAdd ? areaAdd.onDraw : undefined}
      areaSelection={areaAdd ? pendingArea : undefined}
      liveSpan={liveSpan}
      snapReflowRef={snapReflowRef}
    >
      {orderedInstances.map((instance) => (
        <SortableGridItem
          key={instance.id}
          id={instance.id}
          externalItem={dragItem ? dragItem(instance) : undefined}
        >
          {({ dragHandleProps, isDragging, isResizing, w, h }) => (
            // Every placed widget gets its identity as kit context — the one
            // instance-identity channel in the system (custom renderCard included).
            <WidgetInstanceProvider instanceId={instance.id} config={instance.config}>
              {card({
                instance,
                canManage,
                canArrange: arrange,
                consumeOnly,
                dragHandleProps,
                isDragging,
                isResizing,
                liveW: w,
                liveH: h,
                onMaximize: onMaximize ? () => onMaximize(instance) : undefined,
                onConfigure: onConfigure ? () => onConfigure(instance) : undefined,
                onDelete: onDelete ? () => onDelete(instance.id) : undefined,
                onDuplicate: onDuplicate ? () => onDuplicate(instance) : undefined,
                onReplace: onReplace ? () => onReplace(instance) : undefined,
                onConvertToCustom: onConvertToCustom
                  ? () => onConvertToCustom(instance)
                  : undefined,
                onRename: onRename ? (title) => onRename(instance.id, title) : undefined,
                onConfigChange: onConfigChange
                  ? (patch) => onConfigChange(instance.id, patch)
                  : undefined,
                onResetSize: () => resetSize(instance.id),
                widthOptions: arrange ? widthOptions : undefined,
                onWidthChange:
                  arrange && widthOptions?.length
                    ? (width) => changeWidth(instance.id, width)
                    : undefined,
                onContentHeightChange: autoIds.has(instance.id)
                  ? (px, opts) => handleContentHeight(instance.id, px, opts)
                  : undefined,
              })}
            </WidgetInstanceProvider>
          )}
        </SortableGridItem>
      ))}
    </SortableGrid>
  );
}
